import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import { PAGE_SIZE_DIMENSIONS, PAGE_SIZE_A4 } from '../../../../utilities/constants/pageSizeConstants';

const EMPTY_ARRAY = [];

const PageGridOverlay = ({ transform }) => {
  const pageSize = useSelector((state) => state.view.pageSize);
  const nodes = useSelector((state) => state.node.nodes) || EMPTY_ARRAY;
  const edges = useSelector((state) => state.edge.edges) || EMPTY_ARRAY;
  const { width, height } = PAGE_SIZE_DIMENSIONS[pageSize] || PAGE_SIZE_DIMENSIONS[PAGE_SIZE_A4];
  const [container, setContainer] = useState(null);
  const [viewport, setViewport] = useState({ width: 0, height: 0 });
  const [pageLabels, setPageLabels] = useState({}); // key: "ix,iy" -> alphabetic label
  const labelCounterRef = useRef(0);

  const toAlpha = (n) => {
    const A = 'A'.charCodeAt(0);
    let s = '';
    n = Math.max(0, n);
    do {
      s = String.fromCharCode(A + (n % 26)) + s;
      n = Math.floor(n / 26) - 1;
    } while (n >= 0);
    return s;
  };

  const alphaToNumber = (label) => {
    if (!label || typeof label !== 'string') return 0;
    const chars = label.toUpperCase().split('');
    return chars.reduce((acc, ch) => acc * 26 + (ch.charCodeAt(0) - 64), 0);
  };

  const formatPageLabel = (alphaLabel) => {
    const number = alphaToNumber(alphaLabel);
    return `Página ${number || 1}`;
  };

  useEffect(() => {
    const elem = document.querySelector('.react-flow');
    if (elem) {
      setContainer(elem);
      const update = () => {
        const rect = elem.getBoundingClientRect();
        setViewport({ width: rect.width, height: rect.height });
      };
      update();
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
  }, []);

  const { x = 0, y = 0, zoom = 1 } = transform || {};

  useEffect(() => {
    // transform updates tracked silently; no logging in production
  }, [x, y, zoom]);

  // Which pages (ix, iy) are used by nodes or edges?
  const usedPages = useMemo(() => {
    const set = new Set();
    const add = (ix, iy) => set.add(`${ix},${iy}`);
    nodes.forEach((n) => {
      const nx = Number(n?.position?.x);
      const ny = Number(n?.position?.y);
      if (!Number.isFinite(nx) || !Number.isFinite(ny)) return;
      add(Math.floor(nx / width), Math.floor(ny / height));
    });
    // Include edges by segment coverage
    const posById = new Map(
      nodes.map((n) => {
        const nx = Number(n?.position?.x);
        const ny = Number(n?.position?.y);
        return [n.id, { x: Number.isFinite(nx) ? nx : null, y: Number.isFinite(ny) ? ny : null }];
      }),
    );
    const coverRect = (x1, y1, x2, y2) => {
      const left = Math.min(x1, x2);
      const right = Math.max(x1, x2);
      const top = Math.min(y1, y2);
      const bottom = Math.max(y1, y2);
      const minX = Math.floor(left / width);
      const maxX = Math.floor((right - 1) / width);
      const minY = Math.floor(top / height);
      const maxY = Math.floor((bottom - 1) / height);
      for (let iy = minY; iy <= maxY; iy++) {
        for (let ix = minX; ix <= maxX; ix++) add(ix, iy);
      }
    };
    edges.forEach((e) => {
      const start = posById.get(e.source) || { x: null, y: null };
      const end = posById.get(e.target) || { x: null, y: null };
      const sanitizePoint = (pt) => {
        const px = Number(pt?.x);
        const py = Number(pt?.y);
        return { x: Number.isFinite(px) ? px : null, y: Number.isFinite(py) ? py : null };
      };
      const wps = (e?.data?.waypoints || []).map((p) => ({ x: p.x, y: p.y }));
      const pts = [start, ...wps.map(sanitizePoint), end].filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
      if (pts.length < 2) return;
      for (let i = 0; i < pts.length - 1; i++) {
        coverRect(pts[i].x, pts[i].y, pts[i + 1].x, pts[i + 1].y);
      }
    });
    // If the editor is empty, treat the origin tile (0,0) as used so the user still sees a working page.
    if ((nodes?.length || 0) === 0 && (edges?.length || 0) === 0) {
      add(0, 0);
    }
    return set;
  }, [nodes, edges, width, height]);

  // Assign page labels sequentially based on tile position so numbering always stays contiguous.
  useEffect(() => {
    if (!usedPages) return;
    const used = Array.from(usedPages);
    // Sort tiles deterministically by their coordinates (left-to-right, top-to-bottom)
    const ordered = used
      .map((key) => {
        const [ix, iy] = key.split(',').map(Number);
        return { key, ix: Number.isFinite(ix) ? ix : 0, iy: Number.isFinite(iy) ? iy : 0 };
      })
      .sort((a, b) => (a.ix !== b.ix ? a.ix - b.ix : a.iy - b.iy))
      .map((entry) => entry.key);

    const next = {};
    labelCounterRef.current = 0;
    ordered.forEach((key, idx) => {
      next[key] = toAlpha(idx);
      labelCounterRef.current = idx + 1;
    });

    if (ordered.length === 0) {
      labelCounterRef.current = 0;
    }
    setPageLabels((previous) => (
      JSON.stringify(next) !== JSON.stringify(previous) ? next : previous
    ));
  }, [usedPages]);

  // Determine visible page index range
  const visiblePages = useMemo(() => {
    const vw = viewport.width || 0;
    const vh = viewport.height || 0;
    if (vw === 0 || vh === 0 || zoom === 0) return [];

    const leftFlow = -x / zoom;
    const topFlow = -y / zoom;
    const rightFlow = (vw - x) / zoom;
    const bottomFlow = (vh - y) / zoom;

    const minIx = Math.floor(leftFlow / width) - 1;
    const maxIx = Math.floor((rightFlow - 1) / width) + 1;
    const minIy = Math.floor(topFlow / height) - 1;
    const maxIy = Math.floor((bottomFlow - 1) / height) + 1;

    const pages = [];
    for (let iy = minIy; iy <= maxIy; iy++) {
      for (let ix = minIx; ix <= maxIx; ix++) {
        pages.push({ ix, iy });
      }
    }
    return pages;
  }, [viewport, x, y, zoom, width, height]);

  const overlay = (
    <div
      className="page-grid-overlay ux-only"
      style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        backgroundRepeat: 'repeat',
        backgroundSize: `${width * zoom}px ${height * zoom}px`,
        backgroundPosition: `${x}px ${y}px`,
        backgroundImage: `repeating-linear-gradient(to right, #52E1E9 0, #52E1E9 1px, transparent 1px, transparent ${width}px), repeating-linear-gradient(to bottom, #52E1E9 0, #52E1E9 1px, transparent 1px, transparent ${height}px)`,
      }}
    >
      {/* Page labels for visible tiles (low opacity) */}
      {visiblePages.map(({ ix, iy }) => {
        const key = `${ix},${iy}`;
        const labelIndex = pageLabels[key];
        if (labelIndex == null) return null;
        const label = formatPageLabel(labelIndex);
        if (!label) return null;
        const left = ix * width * zoom + x + 8; // 8px padding from top-left
        const top = iy * height * zoom + y + 4;
        return (
          <div key={`lbl-${key}`} style={{ position: 'absolute', left, top, color: '#000', opacity: 0.25, fontSize: 10, fontWeight: 600 }}>
            {`${label}`}
          </div>
        );
      })}

      {visiblePages.map(({ ix, iy }) => {
        const key = `${ix},${iy}`;
        const isUsed = usedPages.has(key);
        if (isUsed) return null;
        const left = ix * width * zoom + x;
        const top = iy * height * zoom + y;
        const w = width * zoom;
        const h = height * zoom;
        return (
          <div
            key={key}
            className="gray-overlay ux-only"
            style={{
              position: 'absolute',
              left,
              top,
              width: w,
              height: h,
              background: 'rgba(0, 0, 0, 0.08)',
              zIndex: 0,
            }}
          />
        );
      })}
    </div>
  );

  return container ? createPortal(overlay, container) : null;
};

export default PageGridOverlay;
